import clsx from 'clsx';
import { Activity, useState, useEffect, useCallback } from 'react';

import { IconTrash } from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import ConfirmationModal from '@/components/ConfirmationModal';
import EmptyState from '@/components/EmptyState';
import { Switch } from '@/components/Switch';
import TagCapsule from '@/components/TagCapsule';
import TimeInput from '@/components/TimeInput/TimeInput';
import {
  getCommonStatusLabel,
  getCommonStatusBadgeClass,
} from '@/constants/constant';
import { ECommonStatus } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import {
  getParentDevices,
  deleteParentDevice,
  getParentScreenTime,
  updateParentScreenTime,
  toggleParentDeviceStatus,
  getParentScreenTimeGraph,
} from '@/service/controlService';
import { useParentStore } from '@/store/useParentStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import { IParentDevice, IParentScreenTimeGraphItem } from '@/types/parent';
import { formatLastActive } from '@/utils';

import ScreenTimeSummaryChart from './components/ScreenTimeSummaryChart';

// ─── Constants ────────────────────────────────────────────────────────────────

const SCREEN_TIME_FILTER_MAP: Record<string, number> = {
  'this-week': 1,
  'last-week': 2,
  'this-month': 3,
  'last-month': 4,
};

// ─── Validation ───────────────────────────────────────────────────────────────

interface IValidationErrors {
  time: string | null;
  limit: string | null;
}

const toMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const validateScreenTime = (
  startTime: string,
  endTime: string,
  dailyLimitHours: number
): IValidationErrors => {
  const errors: IValidationErrors = { time: null, limit: null };

  if (!startTime || !endTime) return errors;

  if (startTime >= endTime) {
    errors.time = 'parent-controls.screen-time.error.invalid-time-range';
    return errors;
  }

  if (dailyLimitHours > 0) {
    const activeMinutes = toMinutes(endTime) - toMinutes(startTime);
    if (activeMinutes < dailyLimitHours * 60) {
      errors.limit =
        'parent-controls.screen-time.error.limit-exceeds-active-hours';
    }
  }

  return errors;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

interface IDeviceRowProps {
  device: IParentDevice;
  onToggle: (device: IParentDevice) => void;
  onDelete: (device: IParentDevice) => void;
  isLast: boolean;
  lastActiveLabel: string;
}

const DeviceRow = ({
  device,
  onToggle,
  onDelete,
  isLast,
  lastActiveLabel,
}: IDeviceRowProps) => {
  const { formatText } = useFormatText();
  const status = device.is_block
    ? ECommonStatus.IN_ACTIVE
    : ECommonStatus.ACTIVE;

  return (
    <div
      className={clsx(
        'flex flex-col sm:flex-row sm:items-center gap-4 py-6',
        !isLast && 'border-b border-neutral-light-200'
      )}
      data-testid="device-row"
    >
      {/* Device info */}
      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <p className="text-base font-semibold text-neutral-primary leading-6">
          {device.device_name}
        </p>
        <p className="text-sm text-neutral-secondary leading-5">
          {lastActiveLabel}:{' '}
          {formatLastActive(
            device.last_active_at ? Number(device.last_active_at) : 0,
            formatText
          )}
        </p>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4 shrink-0">
        <TagCapsule
          value={formatText(getCommonStatusLabel(status))}
          className={getCommonStatusBadgeClass(status)}
        />

        <Switch
          id={`device-toggle-${device.id}`}
          checked={!device.is_block}
          onChange={() => onToggle(device)}
        />
        <Button
          variant="unstyled"
          onClick={() => onDelete(device)}
          className="text-error-secondary hover:text-error-primary transition-colors cursor-pointer"
          aria-label={`Delete ${device.device_name}`}
          icon={<IconTrash size={20} />}
        />
      </div>
    </div>
  );
};

// ─── Screen Time Slider ───────────────────────────────────────────────────────

interface IScreenTimeSliderProps {
  value: number;
  onChange: (val: number) => void;
  label: string;
}

const ScreenTimeSlider = ({
  value,
  onChange,
  label,
}: IScreenTimeSliderProps) => {
  const percentage = (value / 5) * 100;

  return (
    <div className="flex flex-col gap-12 w-full">
      <p className="text-base text-neutral-primary leading-6">{label}</p>
      {/* Slider wrapper with badge */}
      <div className="relative">
        {/* Badge above thumb */}
        <div
          className="absolute -top-8 -translate-x-1/2 bg-brand-light-50 text-brand-secondary text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
          style={{ left: `${percentage}%` }}
        >
          {value} hr
        </div>
        {/* Slider track + thumb */}
        <div className="relative h-2 rounded-full bg-neutral-light-50 w-full">
          <div
            className="absolute left-0 top-0 h-2 rounded-full bg-brand-default"
            style={{ width: `${percentage}%` }}
          />
          <input
            type="range"
            min={0}
            max={5}
            step={0.5}
            value={value}
            onChange={(e) => onChange(Number(e.target.value))}
            className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
            data-testid="daily-limit-slider"
          />
          {/* Thumb */}
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 size-4 rounded-full bg-white border-4 border-brand-default pointer-events-none"
            style={{ left: `${percentage}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// ─── Parent Controls Page ─────────────────────────────────────────────────────

const ParentControls = () => {
  const { formatText } = useFormatText();
  const { setTitle } = usePageTitle();
  const { selectedChild } = useParentStore();
  const { settings } = useSettingsStore();
  const language = settings?.language;
  const selectedChildId = selectedChild?.id;

  useEffect(() => {
    setTitle(formatText('parent-controls.title'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live time values — source of truth for save/validation.
  // Updated via TimeInput's onChange callback so we don't depend on
  // RHF's hidden-input event trick which doesn't reliably update getValues().
  const [activeHoursStart, setActiveHoursStart] = useState('');
  const [activeHoursEnd, setActiveHoursEnd] = useState('');

  // Seed state passed as the initial `value` prop to TimeInput once after API load.
  // Never updated again, so there's no feedback loop.
  const [initialTimes, setInitialTimes] = useState({ start: '', end: '' });
  const [isDirty, setIsDirty] = useState(false);

  // Screen Time state
  const [dailyLimit, setDailyLimit] = useState<number>(0);
  const [lockAfterLimit, setLockAfterLimit] = useState<boolean>(false);
  const [isLoadingScreenTime, setIsLoadingScreenTime] =
    useState<boolean>(false);
  const [periodFilter, setPeriodFilter] = useState<string>('this-week');
  const [screenTimeGraph, setScreenTimeGraph] = useState<
    IParentScreenTimeGraphItem[]
  >([]);

  // Validation errors
  const [validationErrors, setValidationErrors] = useState<IValidationErrors>({
    time: null,
    limit: null,
  });

  // Device state
  const [devices, setDevices] = useState<IParentDevice[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [toggleModalOpen, setToggleModalOpen] = useState<boolean>(false);
  const [targetDevice, setTargetDevice] = useState<IParentDevice | null>(null);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);

  // ─── Fetch Screen Time ────────────────────────────────────────────────────

  const fetchScreenTime = useCallback(async () => {
    if (!selectedChildId) return;

    setIsLoadingScreenTime(true);
    try {
      const response = await getParentScreenTime(selectedChildId);
      if (response?.status) {
        const data = response?.data;
        const startTime = data?.start_time ?? '';
        const endTime = data?.end_time ?? '';
        const limit = data?.daily_limit ? data.daily_limit / 60 : 0;
        const lock = data?.lock_after_limit ?? false;
        setDailyLimit(limit);
        setLockAfterLimit(lock);
        // Seed both the live state and the TimeInput initial value prop
        setActiveHoursStart(startTime);
        setActiveHoursEnd(endTime);
        setInitialTimes({ start: startTime, end: endTime });
        setIsDirty(false);
      } else {
        // No data (first-time setup or API returned false) — reset to empty state
        setDailyLimit(0);
        setLockAfterLimit(false);
        setActiveHoursStart('');
        setActiveHoursEnd('');
        setInitialTimes({ start: '', end: '' });
        setIsDirty(false);
      }
    } catch (err) {
      console.error('Failed to fetch screen time:', err);
    } finally {
      setIsLoadingScreenTime(false);
    }
  }, [selectedChildId]);

  // ─── Fetch Screen Time Graph ───────────────────────────────────────────────

  const fetchScreenTimeGraph = useCallback(async () => {
    if (!selectedChildId) return;

    const filter = SCREEN_TIME_FILTER_MAP[periodFilter] ?? 1;

    try {
      const response = await getParentScreenTimeGraph(selectedChildId, filter);
      if (response?.status) {
        setScreenTimeGraph(response?.data?.activity ?? []);
      }
    } catch (err) {
      console.error('Failed to fetch screen time graph:', err);
    }
  }, [selectedChildId, periodFilter]);

  // ─── Fetch Devices ──────────────────────────────────────────────────────────

  const fetchDevices = useCallback(async () => {
    if (!selectedChildId) return;

    setIsLoadingDevices(true);
    try {
      const response = await getParentDevices(selectedChildId);
      if (response?.status) {
        setDevices(response?.data ?? []);
      }
    } catch (err) {
      console.error('Failed to fetch devices:', err);
    } finally {
      setIsLoadingDevices(false);
    }
  }, [selectedChildId]);

  useEffect(() => {
    fetchScreenTime();
    fetchDevices();
  }, [fetchScreenTime, fetchDevices]);

  useEffect(() => {
    fetchScreenTimeGraph();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChildId, periodFilter, language]);

  // ─── Device Toggle (Block/Unblock) ─────────────────────────────────────────

  const handleToggleClick = (device: IParentDevice) => {
    setTargetDevice(device);
    setToggleModalOpen(true);
  };

  const handleToggleConfirm = async () => {
    if (!targetDevice) return;

    setIsModalLoading(true);
    try {
      const response = await toggleParentDeviceStatus({
        token_id: targetDevice?.id ?? '',
        is_block: !targetDevice.is_block,
      });

      if (response?.status) {
        setDevices((prev) =>
          prev.map((d) =>
            d.id === targetDevice.id
              ? { ...d, is_block: !targetDevice.is_block }
              : d
          )
        );
        setToggleModalOpen(false);
        setTargetDevice(null);
      }
    } catch (err) {
      console.error('Failed to toggle device status:', err);
    } finally {
      setIsModalLoading(false);
    }
  };

  // ─── Device Delete ─────────────────────────────────────────────────────────

  const handleDeleteClick = (device: IParentDevice) => {
    setTargetDevice(device);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!targetDevice) return;

    setIsModalLoading(true);
    try {
      const response = await deleteParentDevice(targetDevice.id);

      if (response?.status) {
        setDevices((prev) => prev.filter((d) => d.id !== targetDevice.id));
        setDeleteModalOpen(false);
        setTargetDevice(null);
      }
    } catch (err) {
      console.error('Failed to delete device:', err);
    } finally {
      setIsModalLoading(false);
    }
  };

  // ─── Save Changes ──────────────────────────────────────────────────────────

  const handleSaveChanges = async () => {
    if (!selectedChildId) return;

    const errors = validateScreenTime(
      activeHoursStart,
      activeHoursEnd,
      dailyLimit
    );

    setValidationErrors(errors);
    if (errors.time || errors.limit) return;

    setIsSaving(true);
    try {
      await updateParentScreenTime({
        child_id: selectedChildId,
        daily_limit: dailyLimit * 60,
        start_time: activeHoursStart || null,
        end_time: activeHoursEnd || null,
        lock_after_limit: lockAfterLimit,
      });
      setIsDirty(false);
    } catch (err) {
      console.error('Failed to save screen time:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDailyLimitChange = (val: number) => {
    setDailyLimit(val);
    setIsDirty(true);
    if (validationErrors.limit) {
      setValidationErrors((prev) => ({ ...prev, limit: null }));
    }
  };

  // ─── Determine toggle modal i18n keys ───────────────────────────────────────

  const isBlockAction = targetDevice ? !targetDevice.is_block : false;
  const toggleModalKeys = isBlockAction
    ? {
        title: 'parent-controls.device-management.block-modal.title',
        prefix: 'common.confirmation.block-prefix',
        suffix: 'parent-controls.device-management.block-modal.suffix',
        confirm: 'parent-controls.device-management.block-modal.confirm',
      }
    : {
        title: 'parent-controls.device-management.unblock-modal.title',
        prefix: 'common.confirmation.unblock-prefix',
        suffix: 'parent-controls.device-management.unblock-modal.suffix',
        confirm: 'parent-controls.device-management.unblock-modal.confirm',
      };

  return (
    <div className="flex flex-col gap-6">
      {/* ── Screen Time Management ─────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-neutral-primary tracking-[-0.2px] leading-7">
          {formatText('parent-controls.screen-time.title')}
        </h2>
        <Button
          label={formatText('common.actions.save-changes')}
          variant="primary"
          loading={isSaving}
          disabled={!isDirty}
          onClick={handleSaveChanges}
        />
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {/* Left card: controls */}
        <div className="border border-neutral-light-200 rounded-2xl min-h-77.5 p-4 flex flex-col gap-10 w-full lg:flex-1 min-w-0">
          {isLoadingScreenTime ? (
            <div className="flex flex-col gap-8 animate-pulse">
              <div className="h-4 bg-neutral-light-200 rounded w-1/3" />
              <div className="h-2 bg-neutral-light-200 rounded w-full" />
              <div className="flex gap-12">
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-neutral-light-200 rounded w-1/2" />
                  <div className="h-10 bg-neutral-light-200 rounded" />
                </div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-neutral-light-200 rounded w-1/2" />
                  <div className="h-10 bg-neutral-light-200 rounded" />
                </div>
              </div>
              <div className="h-6 bg-neutral-light-200 rounded w-1/3" />
            </div>
          ) : (
            <>
              {/* Daily Screen Limit */}
              <ScreenTimeSlider
                value={dailyLimit}
                onChange={handleDailyLimitChange}
                label={formatText('parent-controls.screen-time.daily-limit')}
              />

              {/* Active Hours */}
              <div className="flex flex-col gap-2">
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-12 items-start">
                  <TimeInput
                    name="activeHoursStart"
                    label={formatText(
                      'parent-controls.screen-time.active-hours-starts'
                    )}
                    placeholder={formatText(
                      'parent-controls.screen-time.active-hours-starts-placeholder'
                    )}
                    value={initialTimes.start}
                    onChange={(val) => {
                      setActiveHoursStart(val);
                      setIsDirty(true);
                      if (validationErrors.time) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          time: null,
                        }));
                      }
                    }}
                    clearable
                    className="flex-1"
                  />
                  <TimeInput
                    name="activeHoursEnd"
                    label={formatText(
                      'parent-controls.screen-time.active-hours-ends'
                    )}
                    placeholder={formatText(
                      'parent-controls.screen-time.active-hours-ends-placeholder'
                    )}
                    value={initialTimes.end}
                    onChange={(val) => {
                      setActiveHoursEnd(val);
                      setIsDirty(true);
                      if (validationErrors.time) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          time: null,
                        }));
                      }
                    }}
                    clearable
                    className="flex-1"
                  />
                </div>
                {(validationErrors.time || validationErrors.limit) && (
                  <p
                    className="text-sm font-normal text-error-default"
                    role="alert"
                  >
                    {formatText(
                      validationErrors.time || validationErrors.limit || ''
                    )}
                  </p>
                )}
              </div>

              {/* Lock app toggle */}
              <div className="flex items-center gap-1.5">
                <Switch
                  id="lock-app-switch"
                  checked={lockAfterLimit}
                  onChange={(val) => {
                    setLockAfterLimit(val);
                    setIsDirty(true);
                  }}
                />
                <span className="text-sm text-neutral-primary leading-5">
                  {formatText(
                    'parent-controls.screen-time.lock-app-after-limit'
                  )}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Right card: bar chart */}
        <div className="border border-neutral-light-200 rounded-2xl p-4 w-full lg:flex-[1.5] min-w-0">
          <ScreenTimeSummaryChart
            data={screenTimeGraph}
            periodFilter={periodFilter}
            onPeriodChange={setPeriodFilter}
          />
        </div>
      </div>

      {/* ── Device Management ──────────────────────────────────────── */}
      <div className="flex flex-col gap-6">
        {/* Section header */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-neutral-primary tracking-[-0.2px] leading-7">
            {formatText('parent-controls.device-management.title')}
          </h2>
        </div>

        {/* Device list */}
        {isLoadingDevices && (
          <div className="border border-neutral-light-200 rounded-2xl px-6 py-8">
            <div className="flex flex-col gap-4 animate-pulse">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 py-4">
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-neutral-light-200 rounded w-1/3" />
                    <div className="h-3 bg-neutral-light-200 rounded w-1/4" />
                  </div>
                  <div className="h-6 bg-neutral-light-200 rounded w-16" />
                  <div className="h-6 bg-neutral-light-200 rounded w-10" />
                  <div className="h-5 bg-neutral-light-200 rounded w-5" />
                </div>
              ))}
            </div>
          </div>
        )}

        {!isLoadingDevices && devices.length === 0 && (
          <EmptyState
            title={formatText('parent-controls.device-management.no-devices')}
            description={formatText(
              'parent-controls.device-management.no-devices-description'
            )}
            className="h-[30vh]!"
          />
        )}

        {!isLoadingDevices && devices.length > 0 && (
          <div className="border border-neutral-light-200 rounded-2xl px-6">
            {devices.map((device, index) => (
              <DeviceRow
                key={device.id}
                device={device}
                onToggle={handleToggleClick}
                onDelete={handleDeleteClick}
                isLast={index === devices.length - 1}
                lastActiveLabel={formatText(
                  'parent-controls.device-management.last-active'
                )}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Delete Device Modal ─────────────────────────────────────── */}
      <Activity mode={deleteModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={deleteModalOpen}
          setIsOpen={setDeleteModalOpen}
          title="parent-controls.device-management.delete-modal.title"
          descriptionPrefix="common.delete-confirmation.prefix"
          descriptionSuffix="parent-controls.device-management.delete-modal.suffix"
          entityName={targetDevice?.device_name}
          confirmLabel={formatText(
            'parent-controls.device-management.delete-modal.confirm'
          )}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setTargetDevice(null)}
          isLoading={isModalLoading}
          isDestructive
        />
      </Activity>

      {/* ── Block/Unblock Device Modal ──────────────────────────────── */}
      <Activity mode={toggleModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={toggleModalOpen}
          setIsOpen={setToggleModalOpen}
          title={toggleModalKeys.title}
          descriptionPrefix={toggleModalKeys.prefix}
          descriptionSuffix={toggleModalKeys.suffix}
          entityName={targetDevice?.device_name}
          confirmLabel={formatText(toggleModalKeys.confirm)}
          onConfirm={handleToggleConfirm}
          onCancel={() => setTargetDevice(null)}
          isLoading={isModalLoading}
          isDestructive={isBlockAction}
        />
      </Activity>
    </div>
  );
};

export default ParentControls;
